/*
  Site header: menu drawer, its submenu panels, the country/language selects,
  and the switch from the translucent overlay state to solid white part way
  down the first section.

  The drawer is positioned from the live bottom edge of the header bar rather
  than a hardcoded offset, because the header is sticky and its distance from
  the top of the viewport changes once the topbar has scrolled away.
*/
if (!customElements.get('site-header')) {
  const TRANSITION_MS = 350;

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-menu-toggle]');
      this.drawer = this.querySelector('[data-menu-drawer]');
      this.bar = this.querySelector('[data-header-bar]');

      this.isOpen = false;

      // Which panel is showing, and what opened it, so focus can go back
      // there rather than to the top of the drawer.
      this.openSub = null;
      this.subTrigger = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);

      if (this.toggle && this.drawer) {
        this.toggle.addEventListener('click', () => {
          this.isOpen ? this.close() : this.show();
        });

        this.drawer.addEventListener('click', this.onDrawerClick.bind(this));

        document.addEventListener('keydown', this.onKeydown);
      }

      /*
        The panels ship with the hidden attribute and it is taken off here,
        once, on load. From now on they are held off screen by a transform
        and taken out of reach with visibility, which is what makes the
        slide smooth: an element coming back from display none has no
        previous position, so the browser has nothing to animate from and
        the first frame lands wherever the final one is.

        The attribute is worth shipping anyway. Without this script the
        panels cannot be opened, so they should not be reachable by tab or
        read out by a screen reader.
      */
      this.drawer.querySelectorAll('[data-submenu]').forEach((panel) => {
        panel.removeAttribute('hidden');
      });

      // Country and language pickers are plain forms; submit them on change.
      this.querySelectorAll('[data-localization-select]').forEach((select) => {
        select.addEventListener('change', () => {
          if (select.form) select.form.submit();
        });
      });

      this.setupOverlayState();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      clearTimeout(this.closeTimer);
      document.body.classList.remove('menu-open');
    }

    /* Overlay to solid ---------------------------------------------------- */

    setupOverlayState() {
      if (!this.classList.contains('site-header--overlay')) return;

      this.threshold = (parseFloat(this.dataset.solidAt) || 50) / 100;

      // The section the header sits over is the first one inside main.
      const main = document.getElementById('MainContent');
      this.overlaid = main ? main.firstElementChild : null;

      this.scrollTicking = false;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.onScroll();
    }

    onScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;

      requestAnimationFrame(() => {
        const height = this.overlaid ? this.overlaid.offsetHeight : window.innerHeight;
        this.classList.toggle('is-solid', window.scrollY > height * this.threshold);
        this.scrollTicking = false;
      });
    }

    /* Focus ---------------------------------------------------------------- */

    /*
      Every focus call in this file goes through here, and preventScroll is
      the whole reason it exists.

      Focusing an element normally scrolls it into view. The submenu panels
      sit a full drawer width to the right while they wait, inside a
      container with overflow hidden, so the only way the browser can bring
      one into view is to jump the container sideways. That happens on the
      first frame, cancels the slide, and reads as a flash followed by the
      panel snapping into place.

      preventScroll leaves the scroll position alone and lets the transition
      do the moving. Supported everywhere that matters, and harmless where
      it is not: the option is ignored and focus still lands.
    */
    focusQuietly(element) {
      if (!element) return;

      try {
        element.focus({ preventScroll: true });
      } catch (error) {
        element.focus();
      }
    }

    /* Clicks -------------------------------------------------------------- */

    onDrawerClick(event) {
      if (event.target.hasAttribute('data-menu-overlay')) {
        this.close();
        return;
      }

      const opener = event.target.closest('[data-submenu-open]');
      if (opener) {
        this.showSub(opener.dataset.submenuOpen, opener);
        return;
      }

      if (event.target.closest('[data-submenu-close]')) this.hideSub();
    }

    /* Submenus ------------------------------------------------------------ */

    /*
      Opening is one class on the panel and one attribute on the header. The
      attribute is what moves the list underneath: the two movements are
      described in CSS and start on the same frame, which is the only way
      they stay in step. Driving either from JS frame by frame is what makes
      this kind of thing stutter.
    */
    showSub(index, trigger) {
      const panel = this.drawer.querySelector(`[data-submenu="${index}"]`);
      if (!panel) return;

      // A second category tapped while one is open: the old panel goes
      // without animating, so the two do not cross over each other.
      if (this.openSub && this.openSub !== panel) this.hideSub(true);

      this.openSub = panel;
      this.subTrigger = trigger || null;

      // A panel opened, scrolled through and closed keeps its scroll
      // position, so it would slide back in part way down its own list.
      panel.scrollTop = 0;

      panel.classList.add('is-open');
      this.setAttribute('menu-sub-open', '');

      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      // Focus lands on Back, which is both the first control in the panel
      // and the way out of it.
      this.focusQuietly(panel.querySelector('[data-submenu-close]'));
    }

    hideSub(silent) {
      const panel = this.openSub;
      if (!panel) return;

      const trigger = this.subTrigger;

      this.openSub = null;
      this.subTrigger = null;

      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      panel.classList.remove('is-open');
      this.removeAttribute('menu-sub-open');

      /*
        silent is for the drawer closing around it, and for one panel
        replacing another. Moving focus then would pull it somewhere the
        shopper is not looking.
      */
      if (!silent) this.focusQuietly(trigger);
    }

    /* Drawer -------------------------------------------------------------- */

    position() {
      if (!this.bar || !this.drawer) return;
      const bottom = Math.max(this.bar.getBoundingClientRect().bottom, 0);
      this.drawer.style.setProperty('--menu-top', `${bottom}px`);
    }

    show() {
      clearTimeout(this.closeTimer);
      this.removeAttribute('menu-closing');

      this.position();
      this.isOpen = true;
      this.setAttribute('menu-open', '');
      this.toggle.setAttribute('aria-expanded', 'true');

      // Locking the page also keeps the drawer's top offset valid while open.
      document.body.classList.add('menu-open');

      // Same reason as the panels: the drawer starts off to the left, so
      // focusing into it normally would drag the page across to reach it.
      this.focusQuietly(this.drawer.querySelector('a, button'));
    }

    close() {
      /*
        Any open panel is dropped as the drawer goes, so the menu is back at
        its top level next time it opens. Leaving it open would mean the
        drawer reopens showing a category list with no explanation of how it
        got there.
      */
      this.hideSub(true);

      this.isOpen = false;
      this.removeAttribute('menu-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
      this.focusQuietly(this.toggle);

      // Keeps the drawer rendered while the scrim fades and the panel slides
      // out. Without this it would be hidden on the same frame and the close
      // would read as instant.
      this.setAttribute('menu-closing', '');
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        this.removeAttribute('menu-closing');
      }, TRANSITION_MS);
    }

    /*
      Escape backs out one level at a time: out of the panel first, then out
      of the menu. Closing everything at once would lose the shopper's place
      for what is usually a mis-hit key.
    */
    onKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;

      if (this.openSub) {
        this.hideSub();
        return;
      }

      this.close();
    }
  }

  customElements.define('site-header', SiteHeader);
}
